import PropTypes from "prop-types";
import { useState, useEffect } from "react";

const OtpModal = ({ state, onClose, onVerify, email, loading, error: propError, onResend }) => {
  const [otp, setOtp] = useState("");
  const [localError, setLocalError] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const handleVerify = async () => {
    if (otp.length !== 6 || !/^\d{6}$/.test(otp)) {
      setLocalError("Please enter a valid 6-digit code.");
      return;
    }
    setLocalError("");
    try {
      await onVerify(otp);
    } catch (err) {
      setLocalError(err.message || "Verification failed.");
    }
  };

  const handleChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(value);
    if (localError) setLocalError("");
  };

  const handleResend = async () => {
    setResendLoading(true);
    setLocalError("");
    try {
      await onResend();
      setCooldown(60);
    } catch (err) {
      setLocalError(err.message || "Failed to resend OTP. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    if (cooldown === 0) return;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    if (state) {
      setCooldown(60);
      setOtp("");
      setLocalError("");
    }
  }, [state]);

  if (!state) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-center mb-4 text-blue-500">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>

          <h3 className="text-xl font-bold text-center text-gray-800 mb-2">Verify Your Email</h3>
          <p className="text-sm text-center text-gray-500 mb-6">
            We&apos;ve sent a 6-digit code to <strong>{email}</strong>. Enter it below to continue.
          </p>

          <div className="mb-4">
            <input
              type="text"
              value={otp}
              onChange={handleChange}
              placeholder="000000"
              className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              maxLength={6}
            />
            {(propError || localError) && <p className="text-red-500 text-sm mt-2">{propError || localError}</p>}
          </div>

          <div className="flex gap-3 mb-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleVerify}
              disabled={loading || otp.length !== 6}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-900 font-medium flex justify-center items-center disabled:opacity-70"
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
              ) : "Verify"}
            </button>
          </div>

          <div className="text-center">
            <button
              onClick={handleResend}
              disabled={cooldown > 0 || resendLoading}
              className="text-sm text-primary hover:underline font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              {resendLoading ? "Sending..." : cooldown > 0 ? `Resend OTP (${cooldown}s)` : "Resend OTP"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

OtpModal.propTypes = {
  state: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onVerify: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onResend: PropTypes.func.isRequired,
};

export default OtpModal;